// The appointmentList catalog's retire switch.
//
// `visible` sat dead in the schema until 2026-09-21 — false on 92 of 93 rows and
// read by nothing, so every treatment showed everywhere regardless of it. It now
// means "offer this for a NEW booking", which is what lets a treatment be retired
// WITHOUT deleting its catalog row. Deleting is the alternative and it is much
// worse: appointmentList is a tableref from appointment, treatment, phone,
// Doctor.except and Employee.except, so a delete orphans every one of them (see
// knowledge/xano/backups/appointmentList_2026-09-21_BEFORE-retire-86.md).
//
// ⚠️ Only the BOOKING pickers may filter on this. Two places must keep the FULL
// catalog, and both are load-bearing:
//
//   1. The id → label lookups (`byId` in BookingCalendar / buildTypeLookup).
//      Filtering there would leave every existing appointment on a hidden row
//      rendering with no treatment name — 2,820 of them for id 53 alone.
//
//   2. `specChoices()` / `exceptChoices()` in lib/dentist.js, which feed the
//      dentist drawer and the ตารางเวร shift editor. `pruneExcept()` DROPS any
//      except whose row is missing from the options, so filtering there would
//      silently un-refuse the 32 doctors and 123 shifts that opt out of id 53 —
//      the next save of an untouched record would quietly widen it.
//
// A missing or undefined flag reads as VISIBLE on purpose: a newly added catalog
// row should appear by default, not vanish because nobody remembered to tick it.
export function isBookableType(row) {
  return row?.visible !== false
}

/* ── the การรักษา picker's vocabulary ────────────────────────────────────────
 * One searchable list instead of a ประเภท → รายละเอียด cascade. Staff know a
 * treatment by its name ("ฟอกสี", "รากเทียม") far more often than they know which
 * ประเภท it files under, and the cascade made them guess that first.
 *
 * Two kinds of entry, because they filter differently and both are wanted:
 *   "<Type> · ทั้งหมด"   → the doctor's SPEC only
 *   "<Type> · <detail>"  → spec AND the doctor's EXCEPT list for that row
 *
 * The label is the lookup key, so it must be built in exactly ONE place —
 * `treatmentLabel` below — and read back the same way. Building it inline at the
 * call site is how the ✓ silently stops matching the current selection.
 * `apptTypes.test.mjs` asserts the round-trip over every option. */
export const ALL_LABEL = 'ทั้งหมด'
// The row that clears the filter. Worded differently from ALL_LABEL on purpose: it
// sits above the headings, where a bare "ทั้งหมด" would read as another type's
// ทั้งหมด row rather than "no filter at all".
export const CLEAR_LABEL = 'ทุกการรักษา'

export function treatmentLabel(type, detail) {
  return `${type} · ${detail || ALL_LABEL}`
}

// [{ value, label, group, type, id }] — a per-type ทั้งหมด row followed by that
// type's rows. `list` / `byType` are the already-FILTERED picker structures, so
// retired rows never reach this.
//
//   value  the unique key, still "<Type> · <detail>" — it is what the closed input
//          shows and what onChange hands back, so it must carry the type
//   label   the row's own text, WITHOUT the type: the heading above it already
//          says "Ortho", and repeating it on all fifteen rows is noise
//   group   the heading — the type
export function buildTreatmentOptions(list, byType) {
  const flat = []
  for (const t of list || []) {
    flat.push({ value: treatmentLabel(t, ''), label: ALL_LABEL, group: t, type: t, id: '' })
    for (const o of (byType?.[t] || [])) {
      flat.push({ value: treatmentLabel(t, o.detail), label: o.detail, group: t, type: t, id: String(o.id) })
    }
  }
  return flat
}

// The label for what is currently selected — '' when nothing is, which is the
// default and means "no treatment filter, show every doctor".
export function currentTreatmentLabel(treatType, treatId, byId) {
  if (!treatType) return ''
  return treatId ? treatmentLabel(treatType, byId?.[String(treatId)]?.detail || '') : treatmentLabel(treatType, '')
}
